import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { FormHelperText } from '@mui/material';

import ControlledCardCheckbox from '../forms/ControlledCardCheckbox';
import { BodySmallText, Heading4Text } from '../../typography/typography';
import { PolicyRouteType } from '../../modals/policy/ModalPolicy';
import { isWebViewContainer } from '../../../utils/platform.util';
import { logData } from '../../../utils/debug.util';
import { setPolicyRoute } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import { FormLayout } from '../../../global-styles/layout';
import { theme } from '../../../global-styles/colors';
import { getErrorMessage } from '../../../constants/errorMessages';
import { INSURANCE_COMPANIES } from '../../../constants';
import { IResumeProps } from './resume.type';
import { RegisterResumeStyle } from './resume-style';

const RegisterResume: React.FC<IResumeProps> = ({ form, title, description, resumedData, canEdit = true }) => {
  const providerCode = useSelector((state: RootState) => state.general.provider);

  const dispatch = useDispatch();

  const shouldRedirectLOPD = useMemo(() => {
    return providerCode && INSURANCE_COMPANIES?.[providerCode] && providerCode === '00000002';
  }, [providerCode]);

  const { t } = useTranslation(['common', 'auth']);
  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const [cookiesPolicy, privPolicy] = watch(['cookiesPolicy', 'privPolicy']);

  const selectPolicyUrl = (route: PolicyRouteType | undefined) => {
    if (!providerCode) return undefined;
    if (route === 'privacy') {
      return INSURANCE_COMPANIES?.[providerCode]?.privacy;
    } else if (route === 'cookies') {
      return INSURANCE_COMPANIES?.[providerCode]?.cookies;
    }
    return INSURANCE_COMPANIES?.[providerCode]?.legal;
  };

  const handlePolicyModal = (route: PolicyRouteType | undefined) => {
    const url = selectPolicyUrl(route);
    if (!isWebView && providerCode === '00000002' && route !== 'lopd') {
      window.open(url, '_blank');
    } else {
      dispatch(setPolicyRoute({ policy: route }));
    }
  };

  const isWebView = isWebViewContainer();

  useEffect(() => {
    logData(`${isWebView ? 'WebView' : 'Movile/Desktop'} para ${providerCode}`);
  }, [isWebView, providerCode]);

  return (
    <RegisterResumeStyle>
      {title && (
        <Heading4Text className="m-12px" weight={600}>
          {title}
        </Heading4Text>
      )}
      {description && (
        <BodySmallText color={theme.type.secondary} className="m-24px">
          {description}
        </BodySmallText>
      )}
      <FormLayout data-cy="register-resume">
        {resumedData?.()}
        {canEdit && (
          <>
            <ControlledCardCheckbox
              cardCheckboxProps={{
                active: privPolicy,
                dataCy: 'privPolicy',
                children: (
                  <p>
                    {t('common:legal.privacy.0')}
                    <button className="link" onClick={() => handlePolicyModal('privacy')}>
                      {t('common:legal.privacy.1')}
                    </button>
                    {t('common:legal.privacy.2')}
                    <button className="link" onClick={() => handlePolicyModal('legal')}>
                      {` ${t('common:legal.privacy.3')}`}
                    </button>
                    {`${t('common:legal.privacy.4')} `}
                  </p>
                ),
              }}
              controllerProps={{
                control,
                name: 'privPolicy',
                rules: {
                  required: true,
                },
              }}
            />
            {errors.privPolicy?.type && (
              <FormHelperText error>{getErrorMessage(t, errors.privPolicy?.type)}</FormHelperText>
            )}
          </>
        )}
        {canEdit && (
          <>
            <ControlledCardCheckbox
              cardCheckboxProps={{
                active: cookiesPolicy,
                dataCy: 'cookiesPolicy',
                children: (
                  <p>
                    {`${t('common:legal.lopd.0')} `}
                    {shouldRedirectLOPD ? (
                      <a
                        href="https://www.occident.com/clausula-proteccion-de-datos-comercial"
                        target="_blank"
                        rel="noreferrer"
                        className="link"
                        style={{ display: 'inline' }}
                      >
                        {t('common:legal.lopd.1')}
                      </a>
                    ) : (
                      <button className="link" onClick={() => handlePolicyModal('lopd')}>
                        {t('common:legal.lopd.1')}
                      </button>
                    )}
                  </p>
                ),
              }}
              controllerProps={{
                control,
                name: 'cookiesPolicy',
                rules: {
                  required: true,
                },
              }}
            />
            {errors.cookiesPolicy?.type && (
              <FormHelperText error>{getErrorMessage(t, errors.cookiesPolicy?.type)}</FormHelperText>
            )}
          </>
        )}
      </FormLayout>
    </RegisterResumeStyle>
  );
};

export default RegisterResume;
